import Vue from "vue";
import { mergeImages, DataURIToBlob } from "~/utils";
import googleAnalyticsEvents from "~/mixins/googleAnalyticsEvents";
import facebookPixelEvents from "~/mixins/facebookPixelEvents";
import useEvents from "~/mixins/useEvents";

/**
 * Parse options from data attribute
 */
function parseOptions(optionsData) {
  if (!optionsData) {
    return {};
  }
  try {
    return JSON.parse(optionsData);
  } catch (err) {
    console.warn("Invalid options data:", err);
    return {};
  }
}

/**
 * Validate product - check if allow_buy_free product can be added
 */
function validateProduct(store, sku, productData) {
  const allowBuyFree = productData?.allow_buy_free || false;

  if (!allowBuyFree) {
    return { canAdd: true };
  }

  const cartItems = store.getters["cart/getCartItems"] || [];
  const existingItem = cartItems.find(
    (item) => item.configuration_id === sku || item.sku === sku
  );

  if (existingItem) {
    return {
      canAdd: false,
      reason: "PRODUCT_ALREADY_IN_CART",
    };
  }

  return { canAdd: true };
}

/**
 * Get product configuration by SKU
 */
async function getConfiguration(api, sku) {
  const { data: configResponse } = await api.products.getConfiguration(sku);
  const configurationInfo = configResponse.data;

  if (!configurationInfo) {
    throw new Error("Configuration not found");
  }

  return configurationInfo;
}

/**
 * Get product data by product ID
 */
async function getProduct(api, productId) {
  if (!productId) {
    return null;
  }

  try {
    const { data: productResponse } = await api.products.getProduct(productId);
    return productResponse.data || null;
  } catch (err) {
    console.warn("Failed to fetch product data:", err);
    return null;
  }
}

/**
 * Get both configuration and product data
 */
async function getConfigurationAndProduct(api, sku) {
  const configurationInfo = await getConfiguration(api, sku);
  const productId = configurationInfo.product_id;
  const productData = productId ? await getProduct(api, productId) : null;

  return {
    configurationInfo,
    productData: productData || {},
  };
}

/**
 * Prepare image data for cart item
 */
async function prepareImageData(configurationInfo) {
  const imageIdList =
    configurationInfo?.medias?.reduce((acc, item) => {
      if (item?.position?.includes("PDP") && item?.file?.id) {
        acc.push(item.file.id);
      }
      return acc;
    }, []) || [];
  const imageId = imageIdList?.[0];

  let cartImage = null;
  if (!imageId) {
    const imageUrl =
      configurationInfo?.medias?.[0]?.file?.url || "/images/no-image.webp";
    cartImage = await mergeImages([
      {
        src: imageUrl,
      },
    ]);
    cartImage = DataURIToBlob(cartImage);
  }

  return {
    imageId,
    imageIdList,
    cartImage,
  };
}

/**
 * Build FormData for adding item to cart
 */
function buildFormData(store, sku, configurationInfo, imageData, options) {
  const { imageId, imageIdList, cartImage } = imageData;

  const formData = new FormData();
  formData.append("cart_unique_id", store.getters.getCartId);
  formData.append("sku", sku);
  formData.append("vendor_model", configurationInfo.vendor_model || "");
  formData.append("quantity", "1");
  formData.append("options", JSON.stringify(options));

  if (imageId) {
    formData.append("image_id", imageId);
    imageIdList.forEach((id) => {
      formData.append("images_ids[]", id);
    });
  } else {
    formData.append("image", cartImage);
  }

  return formData;
}

/**
 * Add item to cart
 */
async function addItemToCart(api, store, formData) {
  const { data: cartResponse } = await api.cart.setCartItem(formData);
  await store.dispatch("cart/setCartInfo", cartResponse.data);
  return cartResponse.data;
}

/**
 * Open cart drawer
 */
function openCartDrawer(store) {
  store.commit("general/TOGGLE_CART_DRAWER", true);
}

/**
 * Set loading state on element
 */
function setLoading(element) {
  element.classList.add("is-loading");
  return element.textContent;
}

/**
 * Remove loading state from element
 */
function removeLoading(element, originalText) {
  element.classList.remove("is-loading");
  if (originalText && element.textContent !== originalText) {
    element.textContent = originalText;
  }
}

/**
 * Check if element is in loading state
 */
function isLoading(element) {
  return element.classList.contains("is-loading");
}

/**
 * Initialize analytics instance
 */
function initAnalytics(store, api, app) {
  const AnalyticsComponent = Vue.extend({
    mixins: [googleAnalyticsEvents, facebookPixelEvents, useEvents],
    computed: {
      currentApp() {
        return app;
      },
    },
  });

  const analyticsInstance = new AnalyticsComponent({
    store,
  });

  // Inject Nuxt plugins required by useEvents mixin
  if (!analyticsInstance.$api && api) {
    const apiToUse = app?.$api || api;
    Object.defineProperty(analyticsInstance, "$api", {
      get() {
        return apiToUse;
      },
      configurable: true,
    });
  }

  if (app) {
    if (app.$cookies && !analyticsInstance.$cookies) {
      Object.defineProperty(analyticsInstance, "$cookies", {
        get() {
          return app.$cookies;
        },
        configurable: true,
      });
    }

    if (app.i18n && !analyticsInstance.$i18n) {
      Object.defineProperty(analyticsInstance, "$i18n", {
        get() {
          return app.i18n;
        },
        configurable: true,
      });
    }

    if (app.router && !analyticsInstance.$route) {
      Object.defineProperty(analyticsInstance, "$route", {
        get() {
          return app.router.currentRoute;
        },
        configurable: true,
      });
    }

    if (app.$config && !analyticsInstance.$config) {
      Object.defineProperty(analyticsInstance, "$config", {
        get() {
          return app.$config;
        },
        configurable: true,
      });
    }
  }

  return analyticsInstance;
}

/**
 * Track add to cart event across all analytics platforms
 */
function trackAddToCart(
  analyticsInstance,
  sku,
  configurationInfo,
  productData
) {
  const configInfo = {
    configuration_id: configurationInfo.configuration_id || sku,
    cost: configurationInfo.cost,
    base_cost: configurationInfo.base_cost,
    id: configurationInfo.id || configurationInfo.product_id,
  };

  analyticsInstance.gaAddToCartEvent(productData, configInfo);
  analyticsInstance.fbAddToCartEvent(productData, configInfo);
  analyticsInstance.addToCartEvent({
    product_id: productData?.id || configurationInfo.product_id || "",
    configuration_id: configInfo.configuration_id || sku,
    collection_id: productData?.collections?.[0]?.id || "",
    product_sku: productData?.vendor_model || "",
    product_price: configInfo.cost || configInfo.base_cost || "",
  });
}

/**
 * Dispatch success event
 */
function dispatchSuccessEvent(sku, configurationInfo, productData) {
  window.dispatchEvent(
    new CustomEvent("global-add-to-cart-success", {
      detail: {
        sku,
        configurationInfo,
        productData,
      },
    })
  );
}

/**
 * Dispatch error event
 */
function dispatchErrorEvent(sku, error) {
  window.dispatchEvent(
    new CustomEvent("global-add-to-cart-error", {
      detail: {
        sku,
        error,
      },
    })
  );
}

/**
 * Handle add to cart action
 */
async function handleAddToCart(api, store, app, analyticsInstance, params) {
  const { sku, element, options = {} } = params;

  if (isLoading(element)) {
    return { success: false, reason: "ALREADY_LOADING" };
  }

  const originalText = setLoading(element);

  try {
    const { configurationInfo, productData } = await getConfigurationAndProduct(
      api,
      sku
    );

    const validation = validateProduct(store, sku, productData);

    if (!validation.canAdd) {
      if (validation.reason === "PRODUCT_ALREADY_IN_CART") {
        removeLoading(element, originalText);
        openCartDrawer(store);
        return {
          success: true,
          skipped: true,
          reason: validation.reason,
        };
      }
      throw new Error(`Validation failed: ${validation.reason}`);
    }

    const imageData = await prepareImageData(configurationInfo);

    const formData = buildFormData(
      store,
      sku,
      configurationInfo,
      imageData,
      options
    );

    await addItemToCart(api, store, formData);
    openCartDrawer(store);

    trackAddToCart(analyticsInstance, sku, configurationInfo, productData);

    dispatchSuccessEvent(sku, configurationInfo, productData);

    return {
      success: true,
      data: { sku, configurationInfo, productData },
    };
  } catch (error) {
    dispatchErrorEvent(sku, error);
    throw error;
  } finally {
    removeLoading(element, originalText);
  }
}

/**
 * Global Add to Cart Plugin
 */
export default ({ app, store }) => {
  if (process.client) {
    if (!app) {
      console.error("global-add-to-cart: app is undefined");
      return;
    }

    const api = app.$api;
    const analyticsInstance = initAnalytics(store, api, app);

    document.addEventListener("click", async (e) => {
      const addToCartEl = e.target.closest("[data-add-to-cart]");
      if (!addToCartEl) {
        return;
      }

      const sku = addToCartEl.getAttribute("data-add-to-cart");
      if (!sku) {
        return;
      }

      if (addToCartEl.tagName === "A" || addToCartEl.closest("a")) {
        e.preventDefault();
      }

      try {
        const optionsData = addToCartEl.getAttribute("data-options");
        const options = parseOptions(optionsData);

        const result = await handleAddToCart(
          api,
          store,
          app,
          analyticsInstance,
          {
            sku,
            element: addToCartEl,
            options,
          }
        );

        if (result.skipped) {
          return;
        }
      } catch (err) {
        console.log("err - ", err?.response?.data?.errors || err);

        const errorMessage =
          err?.response?.data?.errors?.[0]?.message ||
          err?.response?.data?.message ||
          err?.message ||
          app?.i18n?.t("createSessionErrorText") ||
          "Something went wrong. Please try again.";

        if (app?.$alert) {
          app.$alert({
            title: app.i18n?.t("error") || "Error",
            text: errorMessage,
          });
        } else {
          store.dispatch("systemPopup/setDialogState", {
            open: true,
            type: "alert",
            title: app?.i18n?.t("error") || "Error",
            text: errorMessage,
          });
        }
      }
    });
  }
};
